import { useState, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { NoteKey } from '../hooks';

interface Product {
  name: string;
  description: string;
  features: string[];
}

interface ProductsProps {
  title: string;
  subtitle: string;
  products: Product[];
  isVisible: boolean;
  onPlayNote: (note: NoteKey) => void;
}

// Map product index to route slugs
const productRoutes = ['iot', 'light-sound', 'life-nature', 'play-learn'];
// Categories that are coming soon (not yet implemented)
const comingSoonCategories = ['light-sound', 'life-nature', 'play-learn'];

const productIcons: Record<string, ReactNode> = {
  iot: (
    <svg className="w-12 h-12" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="24" cy="24" r="6" />
      <circle cx="24" cy="24" r="14" strokeDasharray="4 4" />
      <circle cx="24" cy="24" r="20" strokeDasharray="2 4" />
      <circle cx="24" cy="8" r="3" fill="currentColor" />
      <circle cx="40" cy="24" r="3" fill="currentColor" />
      <circle cx="24" cy="40" r="3" fill="currentColor" />
      <circle cx="8" cy="24" r="3" fill="currentColor" />
    </svg>
  ),
  lightSound: (
    <svg className="w-12 h-12" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M24 4v6M24 38v6M4 24h6M38 24h6" />
      <circle cx="24" cy="24" r="8" />
      <path d="M10 10l4 4M34 34l4 4M10 38l4-4M34 14l4-4" />
      <path d="M32 20c2 2 2 6 0 8M36 16c4 4 4 12 0 16" strokeLinecap="round" />
    </svg>
  ),
  lifeNature: (
    <svg className="w-12 h-12" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M24 44V24" strokeLinecap="round" />
      <path d="M24 24c-8-8-8-16 0-20 8 4 8 12 0 20z" />
      <path d="M18 30c-6 2-10 0-12-6 4-4 10-2 12 6z" />
      <path d="M30 30c6 2 10 0 12-6-4-4-10-2-12 6z" />
      <circle cx="24" cy="24" r="2" fill="currentColor" />
    </svg>
  ),
  playLearn: (
    <svg className="w-12 h-12" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="8" y="12" width="32" height="24" rx="2" />
      <path d="M20 20v8l7-4-7-4z" fill="currentColor" />
      <path d="M12 40h24" strokeLinecap="round" />
      <path d="M16 36v4M32 36v4" strokeLinecap="round" />
      <circle cx="38" cy="8" r="4" />
      <path d="M38 6v4M36 8h4" strokeLinecap="round" />
    </svg>
  ),
};

const iconKeys = ['iot', 'lightSound', 'lifeNature', 'playLearn'];

export default function Products({ title, subtitle, products, isVisible, onPlayNote }: ProductsProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);

  return (
    <section className="py-32 px-6 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[var(--navy-deep)] via-[var(--navy-mid)] to-[var(--navy-deep)]" />

      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-64 h-64 bg-[var(--cyan-electric)]/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-[var(--amber-warm)]/5 rounded-full blur-3xl" />

      <div className="max-w-6xl mx-auto relative z-10">
        <div
          className={`text-center mb-16 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">{title}</h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">{subtitle}</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((product, i) => {
            const routeSlug = productRoutes[i];
            const isComingSoon = comingSoonCategories.includes(routeSlug);
            const href = isComingSoon ? '#' : `/products/${routeSlug}`;

            const cardContent = (
              <div
                className={`group relative p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm
                  hover:bg-white/10 transition-all duration-500 rgb-border-hover-card h-full
                  ${isComingSoon ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}
                  ${activeIndex === i ? 'ring-2 ring-[var(--cyan-electric)] bg-white/10' : ''}`}
                style={{
                  transitionDelay: `${i * 100}ms`,
                  opacity: isVisible ? (isComingSoon ? 0.6 : 1) : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                }}
                onMouseEnter={() => {
                  setActiveIndex(i);
                  onPlayNote(['C5', 'D5', 'E5', 'G5'][i] as NoteKey);
                }}
                onMouseLeave={() => setActiveIndex(null)}
              >
                {/* Coming soon badge */}
                {isComingSoon && (
                  <div className="absolute top-3 right-3 px-2 py-0.5 text-xs font-medium rounded-full bg-[var(--amber-warm)]/20 text-[var(--amber-warm)]">
                    Coming Soon
                  </div>
                )}

                {/* Icon */}
                <div className="text-[var(--cyan-electric)] mb-4 group-hover:scale-110 transition-transform duration-300">
                  {productIcons[iconKeys[i]]}
                </div>

                {/* Product name */}
                <h3 className="text-xl font-semibold mb-2 group-hover:text-[var(--cyan-electric)] transition-colors">
                  {product.name}
                </h3>

                {/* Description */}
                <p className="text-gray-400 text-sm mb-4">{product.description}</p>

                {/* Features */}
                <ul className="space-y-2">
                  {product.features.map((feature, j) => (
                    <li key={j} className="flex items-center gap-2 text-sm text-gray-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-[var(--cyan-electric)]" />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* Hover glow effect */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[var(--cyan-electric)]/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
              </div>
            );

            if (isComingSoon) {
              return (
                <div
                  key={i}
                  onClick={(e) => e.preventDefault()}
                >
                  {cardContent}
                </div>
              );
            }

            return (
              <Link key={i} to={href}>
                {cardContent}
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
